import { useState } from "react";
import { Sparkles, Loader2, Globe, FileText } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { useSMMGeneratorContext } from "../context/SMMGeneratorContext";

interface GeneratorFormProps {
  onClose?: () => void;
  onGenerated?: () => void;
  compact?: boolean;
}

export const GeneratorForm = ({ onGenerated, compact }: GeneratorFormProps) => {
  const {
    handleGenerate,
    isGenerating,
    hasOpenAIKey,
    manifest,
    businessDescription: initialBusinessDescription,
    websiteUrl: initialWebsiteUrl,
  } = useSMMGeneratorContext();

  const [businessDescription, setBusinessDescription] = useState(initialBusinessDescription);
  const [websiteUrl, setWebsiteUrl] = useState(initialWebsiteUrl);

  const canGenerate = hasOpenAIKey && (businessDescription.trim() || websiteUrl.trim()) && !isGenerating;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canGenerate) return;
    onGenerated?.();
    handleGenerate(businessDescription.trim(), websiteUrl.trim());
  };

  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="smm-business-desc-compact" className="text-xs font-medium">
            Business Description
          </Label>
          <textarea
            id="smm-business-desc-compact"
            value={businessDescription}
            onChange={(e) => setBusinessDescription(e.target.value)}
            placeholder="Describe your business: services, target customers, area, unique selling points..."
            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
            disabled={isGenerating || !hasOpenAIKey}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="smm-website-url-compact" className="text-xs font-medium">
            Website URL
          </Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              id="smm-website-url-compact"
              type="url"
              variant="background"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://yourbusiness.com"
              disabled={isGenerating || !hasOpenAIKey}
              className="pl-8"
            />
          </div>
        </div>

        <Button
          type="submit"
          size="sm"
          disabled={!canGenerate}
          title={!hasOpenAIKey ? "An OpenAI API key is required" : undefined}
          className="gap-1.5"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Re-generate
        </Button>
      </form>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <FileText className="h-4.5 w-4.5 text-primary" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Generate Manifest</h3>
          <p className="text-xs text-muted-foreground">
            Provide a business description, a website URL, or both.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="smm-business-desc" className="text-sm font-medium">
            Business Description
          </Label>
          <textarea
            id="smm-business-desc"
            value={businessDescription}
            onChange={(e) => setBusinessDescription(e.target.value)}
            placeholder="Describe your business: services, target customers, area, unique selling points..."
            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
            disabled={isGenerating || !hasOpenAIKey}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="smm-website-url" className="text-sm font-medium">
            Website URL
          </Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              id="smm-website-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://yourbusiness.com"
              disabled={isGenerating || !hasOpenAIKey}
              className="pl-8"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={!canGenerate}
          title={!hasOpenAIKey ? "An OpenAI API key is required" : undefined}
          className="gap-2"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              {manifest ? "Re-generate" : "Generate Manifest"}
            </>
          )}
        </Button>
      </form>
    </div>
  );
};
